import type { Phase } from "@src/packages/cycles/hooks/useCycles";

/**
 * A phase is evidence that a procedure occurred only after it has a real
 * completion window. A start date by itself is a plan or an in-progress
 * record; equal dates are the existing explicit-skip convention.
 */
export function isPerformedProcedurePhase(
  phase: Phase | null | undefined,
): phase is Phase {
  if (!phase?.startDate || !phase.endDate) return false;
  return phase.endDate > phase.startDate;
}

/**
 * Find the latest completed procedure of the requested type without trusting
 * API response order. Repeated procedure events are kept addressable by their
 * dates until the server event graph can provide a dedicated relationship.
 */
export function findLatestPerformedProcedurePhase(
  phases: readonly Phase[],
  procedureTypes: readonly Phase["type"][],
): Phase | null {
  return (
    phases
      .filter(
        (phase) =>
          procedureTypes.includes(phase.type) &&
          isPerformedProcedurePhase(phase),
      )
      .sort((a, b) => {
        const dateOrder = (b.startDate ?? "").localeCompare(a.startDate ?? "");
        if (dateOrder !== 0) return dateOrder;
        return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
      })[0] ?? null
  );
}
